import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import FullscreenButton from '../../components/FullscreenButton';
import useProjectionZoom from '../../utils/useProjectionZoom';
import { sound } from '../../utils/sound';
import { connectSocket } from '../../utils/socket';
import QUIZ_THEMES from '../../utils/quizThemes';
import api from '../../utils/api';

const OPTION_KEYS = ['a', 'b', 'c', 'd'];

export default function GuestDisplayPage() {
  const { pin } = useParams();
  const zoom = useProjectionZoom();

  const [quizInfo, setQuizInfo] = useState(null);
  const [loadError, setLoadError] = useState('');

  // Spectator state
  const [status, setStatus] = useState('lobby'); // lobby | active | finished
  const [question, setQuestion] = useState(null);
  const [qIndex, setQIndex] = useState(0);
  const [qTotal, setQTotal] = useState(0);
  const [timeLeft, setTimeLeft] = useState(0);
  const [timeLimitSec, setTimeLimitSec] = useState(20);
  const [correctOption, setCorrectOption] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [bgColor, setBgColor] = useState('dark');
  const [bgImage, setBgImage] = useState(null);
  const [customColor, setCustomColor] = useState(null);
  const [participants, setParticipants] = useState([]);

  const timerRef = useRef(null);
  const socketConnected = useRef(false);
  const soundRef = useRef(false);

  const joinUrl = `${window.location.origin}/join/${pin}`;

  // Load quiz info by PIN
  useEffect(() => {
    api.get(`/auth/quiz/${pin}`)
      .then(r => { setQuizInfo(r.data.quiz); soundRef.current = !!(r.data.quiz && r.data.quiz.soundProjector); })
      .catch(() => setLoadError('PIN nicht gefunden oder Quiz nicht aktiv.'));
  }, [pin]);

  // Connect socket when we have sessionId
  useEffect(() => {
    if (!quizInfo?.sessionId || socketConnected.current) return;
    socketConnected.current = true;

    const socket = connectSocket();
    socket.emit('spectator:join', { sessionId: quizInfo.sessionId });

    socket.on('lobby:update', ({ participants: p }) => setParticipants(p));

    socket.on('session:state', ({ status: s, bgColor: bc, bgImage: bi, customColor: cc }) => {
      if (s === 'active') setStatus('active');
      if (bc) setBgColor(bc);
      if (bi) setBgImage(bi);
      if (cc) setCustomColor(cc);
    });

    socket.on('quiz:started', ({ bgColor: bc, bgImage: bi, customColor: cc }) => {
      setStatus('active');
      if (bc) setBgColor(bc);
      if (bi) setBgImage(bi);
      if (cc) setCustomColor(cc);
    });

    socket.on('question:start', ({ index, total, timeLimitSec: tl, question: q }) => {
      setQuestion(q);
      setQIndex(index);
      setQTotal(total);
      setTimeLimitSec(tl);
      setTimeLeft(tl);
      setCorrectOption(null);
      setStatus('active');
      if (soundRef.current) sound.start();

      if (timerRef.current) clearInterval(timerRef.current);
      timerRef.current = setInterval(() => {
        setTimeLeft(prev => {
          if (prev <= 1) { clearInterval(timerRef.current); return 0; }
          return prev - 1;
        });
      }, 1000);
    });

    socket.on('question:end', ({ correctOption: co, leaderboard: lb }) => {
      setCorrectOption(co);
      setLeaderboard(lb);
      if (soundRef.current) sound.reveal();
    });

    socket.on('quiz:finished', ({ leaderboard: lb }) => {
      setLeaderboard(lb);
      setStatus('finished');
      if (timerRef.current) clearInterval(timerRef.current);
    });

    return () => {
      socket.off('lobby:update');
      socket.off('session:state');
      socket.off('quiz:started');
      socket.off('question:start');
      socket.off('question:end');
      socket.off('quiz:finished');
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [quizInfo?.sessionId]);

  const theme = QUIZ_THEMES[bgColor] || QUIZ_THEMES.dark;
  const accent = customColor || quizInfo?.customColor || theme.accent;
  const outerBg = bgImage
    ? { backgroundImage: `url(${bgImage})`, backgroundSize: 'cover', backgroundPosition: 'center' }
    : { background: theme.bg };
  const liveText = bgImage ? '#fff' : theme.text;
  const overlayStyle = bgImage
    ? { minHeight: '100vh', background: 'rgba(0,0,0,0.65)', padding: '32px 24px', color: '#fff' }
    : { minHeight: '100vh', padding: '32px 24px', color: theme.text };

  // Error state
  if (loadError) {
    return (
      <div style={{ minHeight: '100vh', background: '#0D1410', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center', color: '#FFFFFF99', fontSize: 18 }}>{loadError}</div>
      </div>
    );
  }

  // Loading
  if (!quizInfo) {
    return (
      <div style={{ minHeight: '100vh', background: '#0D1410', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div className="spinner" />
      </div>
    );
  }

  // Phase 1: Lobby — show PIN + QR + logo + duel info
  if (status === 'lobby') {
    const isDuel = quizInfo.is_duel;
    const logo1 = quizInfo.mosque_logo;
    const logo2 = quizInfo.duel_mosque_logo;
    const name1 = quizInfo.mosqueName;
    const name2 = quizInfo.duel_mosque_name;

    return (
      <div style={{ minHeight: '100vh', background: '#0D1410', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '32px 24px' }}>
        <FullscreenButton />
        <div style={{ maxWidth: 820, width: '100%', zoom }}>

          {/* Logo area */}
          {isDuel ? (
            /* Duel: two logos vs each other */
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 32, marginBottom: 32 }}>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
                {logo1 ? (
                  <img src={logo1} alt={name1} style={{ width: 80, height: 80, borderRadius: 16, objectFit: 'contain', background: '#fff', padding: 6 }} />
                ) : (
                  <div style={{ width: 80, height: 80, borderRadius: 16, background: `${accent}22`, border: `1px solid ${accent}44`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <span style={{ fontSize: 26, fontWeight: 800, color: accent }}>{(name1 || '?').charAt(0).toUpperCase()}</span>
                  </div>
                )}
                <div style={{ fontSize: 15, fontWeight: 600, color: '#fff', textAlign: 'center', maxWidth: 140 }}>{name1}</div>
              </div>
              <div style={{ fontSize: 28, fontWeight: 800, color: `${accent}99`, letterSpacing: 4 }}>VS</div>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
                {logo2 ? (
                  <img src={logo2} alt={name2} style={{ width: 80, height: 80, borderRadius: 16, objectFit: 'contain', background: '#fff', padding: 6 }} />
                ) : (
                  <div style={{ width: 80, height: 80, borderRadius: 16, background: `${accent}22`, border: `1px solid ${accent}44`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <span style={{ fontSize: 26, fontWeight: 800, color: accent }}>{(name2 || '?').charAt(0).toUpperCase()}</span>
                  </div>
                )}
                <div style={{ fontSize: 15, fontWeight: 600, color: '#fff', textAlign: 'center', maxWidth: 140 }}>{name2 || 'Gegner'}</div>
              </div>
            </div>
          ) : (
            /* Normal quiz: single logo */
            logo1 && (
              <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 28 }}>
                <img src={logo1} alt={name1} style={{ height: 72, maxWidth: 200, objectFit: 'contain', background: '#fff', borderRadius: 14, padding: '8px 16px' }} />
              </div>
            )
          )}

          {/* Title */}
          <div style={{ textAlign: 'center', marginBottom: 40 }}>
            {!isDuel && (
              <div style={{ fontSize: 12, color: `${accent}88`, letterSpacing: 4, textTransform: 'uppercase', marginBottom: 10 }}>
                {name1}
              </div>
            )}
            <h1 style={{ fontFamily: 'Playfair Display, serif', fontSize: 38, color: '#FFFFFF', fontWeight: 700, lineHeight: 1.2, margin: 0 }}>
              {quizInfo.title}
            </h1>
          </div>

          {/* PIN + QR — same height */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 28 }}>
            {/* PIN */}
            <div style={{
              background: 'rgba(255,255,255,0.05)', border: `1px solid ${accent}44`, borderRadius: 20,
              padding: '36px 28px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 14,
            }}>
              <div style={{ fontSize: 11, color: `${accent}88`, letterSpacing: 3, textTransform: 'uppercase' }}>PIN Code</div>
              <div style={{ fontFamily: 'monospace', fontSize: 56, fontWeight: 800, letterSpacing: 12, color: accent, lineHeight: 1 }}>
                {pin}
              </div>
              <div style={{ fontSize: 13, color: '#FFFFFF55' }}>{window.location.origin}/join</div>
            </div>

            {/* QR */}
            <div style={{
              background: 'rgba(255,255,255,0.05)', border: `1px solid ${accent}44`, borderRadius: 20,
              padding: '36px 28px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 14,
            }}>
              <div style={{ fontSize: 11, color: `${accent}88`, letterSpacing: 3, textTransform: 'uppercase' }}>QR Code</div>
              <div style={{ background: '#fff', padding: 10, borderRadius: 12 }}>
                <QRCodeSVG value={joinUrl} size={148} fgColor="#111" bgColor="#ffffff" />
              </div>
              <div style={{ fontSize: 13, color: '#FFFFFF55' }}>Handy scannen → direkt beitreten</div>
            </div>
          </div>

          {/* Participants */}
          {participants.length > 0 && (
            <div style={{ background: 'rgba(255,255,255,0.05)', border: `1px solid ${accent}44`, borderRadius: 16, padding: '20px 24px', marginBottom: 24 }}>
              <div style={{ fontSize: 11, color: `${accent}88`, letterSpacing: 3, textTransform: 'uppercase', marginBottom: 14 }}>
                Teilnehmer ({participants.length})
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, justifyContent: 'center' }}>
                {participants.map((p, i) => (
                  <div key={i} style={{ background: `${accent}22`, color: accent, padding: '5px 14px', borderRadius: 100, fontSize: 14, fontWeight: 500 }}>
                    {p.firstName} {p.lastName}
                    {p.team && <span style={{ opacity: 0.6, fontSize: 12 }}> · {p.team}</span>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Waiting */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10 }}>
            <div className="spinner" style={{ width: 14, height: 14, borderWidth: 2, borderColor: `${accent}33`, borderTopColor: accent }} />
            <span style={{ color: `${accent}88`, fontSize: 14 }}>Warte auf Quiz-Start...</span>
          </div>

        </div>
      </div>
    );
  }

  // Phase 3: Finished
  if (status === 'finished') {
    return (
      <div style={{ minHeight: '100vh', ...outerBg }}>
      <FullscreenButton />
        <div style={overlayStyle}>
          <div style={{ maxWidth: 640, margin: '0 auto', textAlign: 'center' }}>
            <h1 style={{ fontFamily: 'Playfair Display', fontSize: 36, color: liveText, marginBottom: 8 }}>
              Quiz beendet
            </h1>
            <p style={{ color: `${liveText}77`, marginBottom: 40, fontSize: 16 }}>Abschluss-Rangliste</p>
            {leaderboard.slice(0, 10).map((p, i) => (
              <div key={i} style={{
                display: 'flex', alignItems: 'center', gap: 16, padding: '14px 20px',
                background: bgImage ? 'rgba(255,255,255,0.1)' : theme.surface,
                borderRadius: 12, marginBottom: 10,
                border: `1px solid ${bgImage ? 'rgba(255,255,255,0.15)' : theme.border}`,
              }}>
                <div style={{ width: 36, textAlign: 'center', fontWeight: 800, fontSize: i < 3 ? 22 : 16,
                  color: i === 0 ? '#C9A84C' : i === 1 ? '#aaa' : i === 2 ? '#C17B5C' : `${liveText}55` }}>
                  {p.rank}
                </div>
                <div style={{ flex: 1, color: liveText, fontSize: 18, fontWeight: 500 }}>
                  {p.firstName} {p.lastName}
                </div>
                <div style={{ fontWeight: 800, color: accent, fontSize: 20 }}>{p.score}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Phase 2: Active — spectator question view (same style as QuizPage, wider for projection)
  const timerPct = timeLimitSec > 0 ? (timeLeft / timeLimitSec) * 100 : 0;
  const getLang = (q, field) => {
    const lang = navigator.language?.startsWith('sq') ? 'al' : 'de';
    return q[`${field}_${lang}`] || q[`${field}_de`] || '';
  };

  // Stronger overlays for bg image to ensure readability on projection
  const cardBg = bgImage ? 'rgba(0,0,0,0.65)' : theme.surface;
  const cardBorder = bgImage ? 'rgba(255,255,255,0.18)' : theme.border;
  const optionBg = bgImage ? 'rgba(0,0,0,0.55)' : theme.surface;
  const optionBorder = bgImage ? 'rgba(255,255,255,0.28)' : theme.border;

  return (
    <div style={{ minHeight: '100vh', ...outerBg }}>
      <FullscreenButton />
      <div style={overlayStyle}>
        <div style={{ maxWidth: 760, margin: '0 auto', zoom }}>

          {/* Header */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
            <div style={{ fontSize: 13, color: `${liveText}99` }}>
              {quizInfo.title}
            </div>
            {question && (
              <div style={{ fontSize: 13, color: `${liveText}99` }}>
                Frage {qIndex + 1} / {qTotal}
              </div>
            )}
          </div>

          {question && (
            <>
              {/* Timer */}
              <div style={{ marginBottom: 20 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                  <span style={{ fontSize: 13, color: `${liveText}99` }}>Zeit</span>
                  <span style={{ fontSize: 22, fontWeight: 800, color: timerPct < 30 ? '#E05C4B' : liveText }}>
                    {timeLeft}s
                  </span>
                </div>
                <div style={{ height: 8, background: bgImage ? 'rgba(255,255,255,0.2)' : theme.surface, borderRadius: 4, overflow: 'hidden' }}>
                  <div style={{
                    height: '100%', borderRadius: 4, transition: 'width 1s linear, background 0.3s',
                    width: `${timerPct}%`,
                    background: timerPct < 30 ? '#E05C4B' : timerPct < 50 ? '#C9A84C' : accent,
                  }} />
                </div>
              </div>

              {/* Question */}
              <div style={{
                background: cardBg, borderRadius: 16, padding: '28px 24px', marginBottom: 20,
                textAlign: 'center', border: `1px solid ${cardBorder}`,
              }}>
                {question.image_path && (
                  <img
                    src={`/uploads/${question.image_path}`}
                    alt=""
                    style={{ display: 'block', maxWidth: '100%', maxHeight: 260, borderRadius: 10, marginBottom: 16, objectFit: 'contain' }}
                  />
                )}
                <p style={{ fontSize: 24, fontWeight: 600, lineHeight: 1.4, color: '#fff', margin: 0 }}>
                  {getLang(question, 'text')}
                </p>
              </div>

              {/* Options — same structure as QuizPage */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 20 }}>
                {OPTION_KEYS.filter(k => question[`option_${k}_de`]).map(key => {
                  const isCorrect = correctOption && key === correctOption;
                  return (
                    <div key={key} style={{
                      padding: '18px 16px', borderRadius: 12, fontSize: 16,
                      border: `2px solid ${isCorrect ? '#5B8A75' : optionBorder}`,
                      background: isCorrect ? '#5B8A75' : optionBg,
                      color: isCorrect ? '#fff' : '#fff',
                      transition: 'all 0.3s',
                    }}>
                      <span style={{ display: 'block', fontSize: 11, color: isCorrect ? 'rgba(255,255,255,0.75)' : 'rgba(255,255,255,0.55)', marginBottom: 4 }}>
                        {key.toUpperCase()}
                      </span>
                      {getLang(question, `option_${key}`)}
                    </div>
                  );
                })}
              </div>
            </>
          )}

          {/* Leaderboard after question end */}
          {correctOption && leaderboard.length > 0 && quizInfo?.leaderboardProjector !== false && (
            <div style={{ background: cardBg, borderRadius: 14, padding: 20, border: `1px solid ${cardBorder}` }}>
              <h3 style={{ fontSize: 13, color: 'rgba(255,255,255,0.55)', marginBottom: 14, letterSpacing: 2, textTransform: 'uppercase' }}>
                Rangliste
              </h3>
              {leaderboard.slice(0, 8).map((p, i) => (
                <div key={i} style={{
                  display: 'flex', alignItems: 'center', gap: 12, padding: '10px 0',
                  borderBottom: i < Math.min(leaderboard.length, 8) - 1 ? '1px solid rgba(255,255,255,0.08)' : 'none',
                }}>
                  <div style={{ width: 28, textAlign: 'center', fontWeight: 800, fontSize: i < 3 ? 18 : 14,
                    color: i === 0 ? '#C9A84C' : i === 1 ? '#aaa' : i === 2 ? '#C17B5C' : 'rgba(255,255,255,0.35)' }}>
                    {p.rank}
                  </div>
                  <div style={{ flex: 1, color: '#fff', fontSize: 16 }}>
                    {p.firstName} {p.lastName}
                  </div>
                  <div style={{ fontWeight: 700, color: accent, fontSize: 17 }}>{p.score}</div>
                </div>
              ))}
            </div>
          )}

        </div>
      </div>
    </div>
  );
}
